import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signupWithEmail } from '../auth/signup';
import { signInWithGoogle } from '../auth/google';
import { useAuth } from '../auth/hooks';
import { HybridIcon } from '../components/HybridIcon';
import './Auth.css';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/overview');
    }
  }, [isAuthenticated, navigate]);

  const validateForm = (): boolean => {
    if (!email.trim()) {
      alert('Please enter your email');
      return false;
    }
    if (!password.trim()) {
      alert('Please enter a password');
      return false;
    }
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return false;
    }
    if (!displayName.trim()) {
      alert('Please enter your name');
      return false;
    }
    if (!organizationName.trim()) {
      alert('Please enter your organization name');
      return false;
    }
    return true;
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await signupWithEmail({
        email,
        password,
        displayName,
        organizationName,
      });

      if (result.success) {
        alert('Account created successfully! Please check your email to verify your account.');
        navigate('/verify-email');
      } else {
        alert(result.error || 'Signup failed');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      if (result.success) {
        if (result.isNewUser) {
          alert('Account created successfully!');
        }
        navigate('/overview');
      } else {
        alert(result.error || 'Signup failed');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-content">
        <div className="auth-header">
          <HybridIcon name="car" size={64} color="#f4511e" />
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Get started with Path Finder</p>
        </div>

        <form className="auth-form" onSubmit={handleEmailSignup}>
          <div className="auth-input-group">
            <HybridIcon name="person" size={20} color="#666" className="auth-input-icon" />
            <input
              type="text"
              placeholder="Full Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <div className="auth-input-group">
            <HybridIcon name="business" size={20} color="#666" className="auth-input-icon" />
            <input
              type="text"
              placeholder="Organization Name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <div className="auth-input-group">
            <HybridIcon name="mail" size={20} color="#666" className="auth-input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <div className="auth-input-group">
            <HybridIcon name="lock-closed" size={20} color="#666" className="auth-input-icon" />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <div className="auth-input-group">
            <HybridIcon name="lock-closed" size={20} color="#666" className="auth-input-icon" />
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="auth-input"
              disabled={loading}
              required
            />
          </div>

          <button
            type="submit"
            className="auth-button auth-button-primary"
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-small" />
            ) : (
              'Create Account'
            )}
          </button>

          <div className="auth-divider">
            <div className="auth-divider-line" />
            <span className="auth-divider-text">OR</span>
            <div className="auth-divider-line" />
          </div>

          <button
            type="button"
            className="auth-button auth-button-google"
            onClick={handleGoogleSignup}
            disabled={loading}
          >
            <HybridIcon name="logo-google" size={20} color="#fff" />
            <span>Sign Up with Google</span>
          </button>

          <div className="auth-footer">
            <span>Already have an account? </span>
            <Link to="/login" className="auth-link">Sign In</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

